// T013: GameState Model

import type {
  GameState,
  Player,
  Battlefield,
  TurnPhase,
  AttackDeclaration,
  GameAction,
  CardDefinition,
} from '../../types';
import { createBattlefield, getAllUnitsForPlayer } from './Battlefield';
import { getCardScore } from './Card';

export function createInitialGameState(
  player1Config: { id: string; name: string; faction: string },
  player2Config: { id: string; name: string; faction: string }
): GameState {
  const player1: Player = {
    id: player1Config.id,
    name: player1Config.name,
    faction: player1Config.faction,
    deck: [],
    hand: [],
    graveyard: [],
    forest: [],
  };

  const player2: Player = {
    id: player2Config.id,
    name: player2Config.name,
    faction: player2Config.faction,
    deck: [],
    hand: [],
    graveyard: [],
    forest: [],
  };

  return {
    player1,
    player2,
    battlefield: createBattlefield(),
    turnNumber: 1,
    activePlayerId: player1.id,
    currentPhase: 'summon', // First turn skips draw & defense
    attackDeclarations: [],
    countdownTurns: null,
    status: 'playing',
    gameEnded: false,
    winnerId: null,
    actionLog: [],
    tacticsActionUsed: false,
    mechaMergePhaseActive: false,
    mechaMergeUsedThisTurn: false,
  };
}

export function getActivePlayer(state: GameState): Player {
  return state.activePlayerId === state.player1.id
    ? state.player1
    : state.player2;
}

export function getInactivePlayer(state: GameState): Player {
  return state.activePlayerId === state.player1.id
    ? state.player2
    : state.player1;
}

export function getPlayerById(state: GameState, playerId: string): Player | null {
  if (state.player1.id === playerId) return state.player1;
  if (state.player2.id === playerId) return state.player2;
  return null;
}

export function updatePlayer(state: GameState, player: Player): GameState {
  if (state.player1.id === player.id) {
    return { ...state, player1: player };
  }
  if (state.player2.id === player.id) {
    return { ...state, player2: player };
  }
  return state;
}

export function setPhase(state: GameState, phase: TurnPhase): GameState {
  return { ...state, currentPhase: phase };
}

export function switchActivePlayer(state: GameState): GameState {
  const newActiveId =
    state.activePlayerId === state.player1.id
      ? state.player2.id
      : state.player1.id;

  // Increment turn number when switching back to player 1
  const newTurnNumber = newActiveId === state.player1.id
    ? state.turnNumber + 1
    : state.turnNumber;

  return {
    ...state,
    activePlayerId: newActiveId,
    turnNumber: newTurnNumber,
    currentPhase: 'draw',
  };
}

export function addAttackDeclaration(
  state: GameState,
  declaration: AttackDeclaration
): GameState {
  return {
    ...state,
    attackDeclarations: [...state.attackDeclarations, declaration],
  };
}

export function clearAttackDeclarations(state: GameState): GameState {
  return {
    ...state,
    attackDeclarations: [],
  };
}

export function startCountdown(state: GameState): GameState {
  if (state.countdownTurns !== null) return state;
  return { ...state, countdownTurns: 5 };
}

export function decrementCountdown(state: GameState): GameState {
  if (state.countdownTurns === null) return state;
  return {
    ...state,
    countdownTurns: state.countdownTurns - 1,
  };
}

export function isCountdownFinished(state: GameState): boolean {
  return state.countdownTurns !== null && state.countdownTurns <= 0;
}

export function endGame(state: GameState, winnerId: string | null): GameState {
  return {
    ...state,
    gameEnded: true,
    winnerId,
  };
}

export function addAction(state: GameState, action: GameAction): GameState {
  return {
    ...state,
    actionLog: [...state.actionLog, action],
  };
}

export function calculatePlayerScore(
  state: GameState,
  playerId: string,
  definitions: Map<string, CardDefinition>
): number {
  const playerSide = playerId === state.player1.id ? 'player1' : 'player2';
  const units = getAllUnitsForPlayer(state.battlefield, playerSide);
  let score = 0;

  for (const unit of units) {
    score += getCardScore(unit, definitions);
  }

  return score;
}

export function determineWinner(
  state: GameState,
  definitions: Map<string, CardDefinition>
): string | null {
  const score1 = calculatePlayerScore(state, state.player1.id, definitions);
  const score2 = calculatePlayerScore(state, state.player2.id, definitions);

  if (score1 > score2) {
    return state.player1.id;
  } else if (score2 > score1) {
    return state.player2.id;
  } else {
    // Tie: second player (non-first player) wins
    // activePlayerId at game start is the first player; the other is second
    const firstPlayerId = state.player1.id; // player1 is always first by convention
    return firstPlayerId === state.player1.id ? state.player2.id : state.player1.id;
  }
}

export function updateBattlefield(
  state: GameState,
  battlefield: Battlefield
): GameState {
  return { ...state, battlefield };
}
